"use client";

import { useEffect, useState } from "react";

/**
 * The marketing bar's scroll behavior, ported 1:1 from the approved reference
 * export ("Zom Marketing Site.dc.html", the `tick` handler in
 * componentDidMount). The reference drives it from real page scroll precisely
 * so every public page behaves identically (home, company, security), which is
 * why this lives in one hook rather than being re-derived per page.
 *
 * Two behaviors, both taken from the reference verbatim:
 * - past 80px the bar takes its scrim (background, hairline, blur);
 * - it retracts on downward scroll and returns on any upward scroll.
 *
 * Scroll is sampled inside requestAnimationFrame so the hide/show delta is per
 * frame, not per wheel event. The rAF only runs while scrolling; a perpetual
 * loop was keeping the main thread busy on long pages like /security.
 */
export function useMarketingNavScroll() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let lastScroll = window.scrollY;
    let frame = 0;
    let scrolledState = lastScroll > 80;
    let hiddenState = false;

    const tick = () => {
      const current = window.scrollY;
      const delta = current - lastScroll;
      const nextScrolled = current > 80;
      let nextHidden = hiddenState;

      if (delta > 1 && current > 80) {
        nextHidden = true;
      } else if (delta < -1) {
        nextHidden = false;
      }

      if (nextHidden !== hiddenState) {
        hiddenState = nextHidden;
        setHidden(nextHidden);
      }

      if (nextScrolled !== scrolledState) {
        scrolledState = nextScrolled;
        setScrolled(nextScrolled);
      }

      lastScroll = current;
      frame = 0;
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(tick);
    };

    tick();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return { scrolled, hidden, setHidden };
}
